import { getOptionName } from '../../../utils/functions';

// Highest = 1. Tiers a customer can upgrade TO are ranked ABOVE (lower number
// than) their currently-booked tier. Names not in the map (e.g. "Yearbook") rank
// last, so they're never treated as an upgrade target.
const TIER_RANK = { super: 1, deluxe: 2, standard: 3, basic: 4, economy: 5 };
const tierRank = (name) => TIER_RANK[(getOptionName(name)[0] || '').toLowerCase()] ?? 99;
const isUpgradeProduct = (opt) => /upgrade/i.test((opt && opt.name) || '');

// Pick the mock "currently booked" session for preview mode (no real prior purchase):
// prefer Standard, else the first session.
function previewPrior(sessions) {
  return sessions.find((s) => /standard/i.test(s.name)) || sessions[0] || null;
}

// Decides whether the senior session-selection page shows the returning-customer
// UPGRADE view (current sitting + higher tiers, pay the difference) vs the
// new-booking grid -- and which upgrade options to offer.
//
// Returning is keyed on the prior PURCHASE (student-based `priorOrderPackage`) plus
// higher tiers being available -- NOT on dedicated "/upgrade/" PRODUCT options. The
// backend's email-based options filter strips those products out, which used to hide
// the upgrade view entirely for staff-sent upgrade links (the customer landed on the
// full-price new-booking grid instead). The view always offers higher SESSION tiers
// priced as the difference, consistent with the staff Reschedule/Upgrade screen.
//
// preselectUpgradeId (an Option id from a staff-sent SMS link) narrows the offer to
// just that one tier so the customer confirms the upgrade staff chose.
export function selectUpgradeView({
  options = [],
  priorOrderPackage = null,
  preselectUpgradeId = null,
  previewReturning = false,
} = {}) {
  const sessions = options.filter((o) => !isUpgradeProduct(o));
  const upgradeProducts = options.filter(isUpgradeProduct);

  let priorOption = null;
  if (priorOrderPackage) {
    priorOption = options.find((o) => Number(o.id) === Number(priorOrderPackage.optionId)) || null;
  } else if (previewReturning) {
    priorOption = previewPrior(sessions);
  }

  const priorRank = priorOption ? tierRank(priorOption.name) : 99;
  const upgradeSessions = sessions
    .filter((s) => tierRank(s.name) < priorRank)
    .sort((a, b) => tierRank(a.name) - tierRank(b.name)); // best -> worst

  const staffPick = preselectUpgradeId
    ? upgradeSessions.find((s) => String(s.id) === String(preselectUpgradeId)) || null
    : null;
  const offeredUpgrades = staffPick ? [staffPick] : upgradeSessions;

  const isReturning = previewReturning
    ? upgradeProducts.length > 0 || upgradeSessions.length > 0
    : !!priorOrderPackage && (upgradeSessions.length > 0 || upgradeProducts.length > 0);

  const priorPrice = Number(priorOption?.prices?.[0]?.price) || 0;

  return { isReturning, priorOption, priorPrice, sessions, upgradeSessions, offeredUpgrades, staffPick };
}
